// Pure row-shaping for user-entered custom metrics on the KPI dashboard. These
// operate on already-derived column buckets (no cube/React deps) so they are
// unit-testable; connect.ts derives the buckets from the cube rows and calls in.
import {
  bucketValue,
  CUSTOM_KEY_PREFIX,
  type BucketGranularity,
  type CustomMetricValueBucket,
  deriveBucketEnd,
  resolveBucketValue,
  shiftComparisonStart,
} from "~/shared/kpi"
import { type MetricDefinition } from "~/ui-rtk/constants/charts"

/** A KPI table column's period bucket, edges derived from the cube's own dates. */
export interface ColumnBucket {
  key: string
  start: string
  end: string
}

/** Current + prior-period (YoY) value per column for one custom metric. */
export interface CustomMetricSeries {
  current: (number | null)[]
  previous: (number | null)[]
}

export type KpiRow = Record<string, string | number | null | undefined>

/**
 * Buckets one custom metric's stored values onto the column buckets and their
 * prior-period (YoY) counterparts. Additive metrics roll finer entries up into
 * coarser buckets; a value entered at the bucket's own grain takes precedence
 * (see bucketValue). Computed once per metric in useConnect and shared by the
 * row builder and the calc-operand getter.
 *
 * `comparisonStartOf` overrides the default year-back shift for EXPLICIT
 * compare windows (comparisonShiftForWindows) so the previous series reads the
 * same buckets the cube's comparison rows pair with; omitted → the dashboard's
 * YoY rule, unchanged.
 */
export function bucketCustomSeries(
  values: CustomMetricValueBucket[],
  columnBuckets: ColumnBucket[],
  granularity: BucketGranularity,
  additive: boolean,
  comparisonStartOf?: (bucketStart: string) => string,
): CustomMetricSeries {
  const current: (number | null)[] = []
  const previous: (number | null)[] = []
  for (const bucket of columnBuckets) {
    current.push(bucketValue(values, bucket.start, bucket.end, additive))
    const compStart = comparisonStartOf
      ? comparisonStartOf(bucket.start)
      : shiftComparisonStart(bucket.start, granularity)
    previous.push(
      bucketValue(
        values,
        compStart,
        deriveBucketEnd(compStart, granularity),
        additive,
      ),
    )
  }
  return { current, previous }
}

/**
 * Per-column inline-edit gate for one custom metric. A cell is read-only when
 * its displayed value is a roll-up of finer entries that completely tile the
 * bucket: editing it would create an exact coarse row that permanently shadows
 * the finer data (bucketValue's exact-match precedence). Exact rows, empty
 * buckets, and partial roll-ups stay editable — a coarse entry over an
 * incomplete finer group is the established manual-entry semantics.
 */
export function customCellEditability(
  values: CustomMetricValueBucket[],
  columnBuckets: ColumnBucket[],
  additive: boolean,
): boolean[] {
  return columnBuckets.map(bucket => {
    const resolved = resolveBucketValue(
      values,
      bucket.start,
      bucket.end,
      additive,
    )
    return !(resolved.source === "rollup" && resolved.complete)
  })
}

/** YoY-change convention shared with the cube row builders. */
function periodChange(
  currentValue: number | null,
  previousValue: number | null,
): number | null {
  if (previousValue !== null && previousValue !== 0) {
    return (((currentValue ?? 0) - previousValue) / previousValue) * 100
  }
  if ((currentValue ?? 0) === 0 && (previousValue ?? 0) === 0) {
    return 0
  }
  return null
}

// Row builder for user-entered CUSTOM metrics (the `custom.` source). Same row
// shape as processMetrics, but values come from the pre-bucketed series (which
// already applied roll-up + YoY-shift), not a cube column. YoY is fully
// self-contained: there is no `compared.*` twin for user-entered data.
export function processCustomMetrics(
  columnBuckets: ColumnBucket[],
  metrics: MetricDefinition[],
  customSeriesById: Map<string, CustomMetricSeries>,
): KpiRow[] {
  if (columnBuckets.length === 0 || metrics.length === 0) return []

  return metrics.map(metricDef => {
    const series = customSeriesById.get(
      metricDef.key.slice(CUSTOM_KEY_PREFIX.length),
    )
    const row: KpiRow = {
      metric: metricDef.label,
      type: metricDef.type,
      key: metricDef.key,
      suffix: metricDef.suffix,
    }

    columnBuckets.forEach((_bucket, index) => {
      const weekKey = `week_${index + 1}`
      const currentValue = series?.current[index] ?? null
      const previousValue = series?.previous[index] ?? null
      row[weekKey] = currentValue
      row[`${weekKey}_yoy_previous`] = previousValue
      row[`${weekKey}_yoy_change`] = periodChange(currentValue, previousValue)
    })

    return row
  })
}

/**
 * One typed custom metric's display row plus the anchor keys of its primary
 * table (see foldTargetsFor in ~/shared/kpi — e.g. an Ad Spend metric anchors
 * at `metrics.total_ad_spend`, a DTC Ecomm revenue metric at the ecomm total)
 * and the rollup-table fallback anchors used when no table holds a primary.
 */
export interface TypedRowInsertion {
  anchorKeys: readonly string[]
  fallbackAnchorKeys: readonly string[]
  row: KpiRow
}

function findAnchorIndex(
  rows: KpiRow[],
  anchorKeys: readonly string[],
): number {
  return rows.findIndex(row => anchorKeys.includes(row.key as string))
}

function insertAt(rows: KpiRow[], index: number, row: KpiRow): KpiRow[] {
  return [...rows.slice(0, index), row, ...rows.slice(index)]
}

/**
 * Inserts typed custom-metric rows into ONE table's rows, each immediately
 * before the first of its primary anchor keys present — or, failing that, its
 * fallback anchors (so e.g. a Subscription-tagged metric still shows beside
 * the Total Business Revenue row in a group that lacks the subscription
 * total). Purely a DISPLAY operation: the values themselves were already
 * summed into the aggregate columns by the dataset-level fold
 * (applyTypedColumnDeltas), which is what the totals, their YoY cells, WoW
 * rows, charts, and exports read. A table without any anchor is left
 * untouched; `skipKeys` suppresses insertions whose metric a custom-report
 * group already lists explicitly. For the builtin dashboard's multi-table
 * placement, use insertTypedRowsAcrossTables instead.
 */
export function applyTypedRowInsertions(
  baseRows: KpiRow[],
  insertions: TypedRowInsertion[],
  skipKeys?: ReadonlySet<string>,
): KpiRow[] {
  if (insertions.length === 0 || baseRows.length === 0) return baseRows

  let rows = baseRows
  for (const insertion of insertions) {
    if (skipKeys?.has(insertion.row.key as string)) continue
    let anchorIndex = findAnchorIndex(rows, insertion.anchorKeys)
    if (anchorIndex === -1) {
      anchorIndex = findAnchorIndex(rows, insertion.fallbackAnchorKeys)
    }
    if (anchorIndex === -1) continue
    rows = insertAt(rows, anchorIndex, insertion.row)
  }
  return rows
}

/**
 * Places each typed custom-metric row in exactly ONE of the builtin
 * dashboard's tables (`tables` in display order; `eligible: false` marks
 * tables that will not render — unavailable connectors, feature flags).
 * Placement rule per insertion:
 *   1. among eligible tables containing a primary anchor, prefer the first
 *      that does NOT also contain a fallback anchor — the metric's dedicated
 *      table, not the Total Business Revenue rollup (whose rows can contain
 *      the same key, e.g. `metrics.amazon_total_revenue`);
 *   2. with no primary match anywhere, the first eligible table containing a
 *      fallback anchor gets the row (the folded TBR total then visibly
 *      explains itself even though the metric's channel table isn't rendered).
 * Returns one rows-array per input table (input identity when untouched).
 */
export function insertTypedRowsAcrossTables(
  tables: Array<{ rows: KpiRow[]; eligible: boolean }>,
  insertions: TypedRowInsertion[],
): KpiRow[][] {
  const result = tables.map(table => table.rows)
  if (insertions.length === 0) return result

  for (const insertion of insertions) {
    let targetIndex = -1
    let fallbackTargetIndex = -1
    let rollupTargetIndex = -1
    for (let index = 0; index < tables.length; index += 1) {
      if (!tables[index].eligible) continue
      const rows = result[index]
      if (findAnchorIndex(rows, insertion.anchorKeys) !== -1) {
        if (findAnchorIndex(rows, insertion.fallbackAnchorKeys) === -1) {
          targetIndex = index
          break
        }
        if (rollupTargetIndex === -1) rollupTargetIndex = index
      } else if (
        fallbackTargetIndex === -1 &&
        findAnchorIndex(rows, insertion.fallbackAnchorKeys) !== -1
      ) {
        fallbackTargetIndex = index
      }
    }

    if (targetIndex === -1) targetIndex = rollupTargetIndex
    let anchorKeys = insertion.anchorKeys
    if (targetIndex === -1) {
      targetIndex = fallbackTargetIndex
      anchorKeys = insertion.fallbackAnchorKeys
    }
    if (targetIndex === -1) continue

    const anchorIndex = findAnchorIndex(result[targetIndex], anchorKeys)
    result[targetIndex] = insertAt(
      result[targetIndex],
      anchorIndex,
      insertion.row,
    )
  }
  return result
}
